import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, EmptyState, ErrorState, SelectField, Skeleton, TextField, Pill } from "../../components/ui.tsx";
import { ConfirmDialog, Modal } from "../../components/dialog.tsx";
import { get, patch, post, applyFieldErrors, ApiError } from "../../lib/api.ts";
import { planKey, usePlan } from "../../lib/plan.tsx";
import type { Axis, Group } from "../../lib/types.ts";
import { requiredText } from "../../../shared/setup.ts";
import { LIMITS } from "../../../shared/limits.ts";
import { useCommand } from "./shared.tsx";
import { useUrlState } from "../../lib/nav.tsx";
import { ArchivedToggle } from "./shared.tsx";
import { ContextImpact, type Affected } from "../../components/context-impact.tsx";

const axisForm = z.object({
  name: requiredText(LIMITS.name, "Informe o nome do eixo."),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Use uma cor no formato #RRGGBB."),
  editor_group_id: z.string(),
});

export function AxesTab() {
  const plan = usePlan();
  const [view, setView] = useUrlState({ arquivados: "" });
  const showArchived = view.arquivados === "1";
  const axes = useQuery({
    queryKey: planKey(plan.id, "axes", showArchived ? "with-archived" : "active"),
    queryFn: async () => (await get<{ data: Axis[] }>(`/plans/${plan.id}/axes${showArchived ? "?include_archived=true" : ""}`)).data,
  });
  const groups = useQuery({ queryKey: planKey(plan.id, "groups"), queryFn: async () => (await get<{ data: Group[] }>(`/plans/${plan.id}/groups`)).data });
  const [editing, setEditing] = useState<Axis | "new" | null>(null);
  const [archiving, setArchiving] = useState<Axis | null>(null);
  const { run, busy } = useCommand(plan.id);

  if (axes.isPending) return <Skeleton lines={4} />;
  if (axes.isError) return <ErrorState error={axes.error} onRetry={() => void axes.refetch()} />;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">Dimensão: Gestão de Pessoas. Cada eixo tem no máximo um grupo editor; sem grupo, só o instrutor edita.</p>
        <div className="flex flex-wrap items-center gap-3"><ArchivedToggle checked={showArchived} onChange={(v) => setView({ arquivados: v ? "1" : "" })} /><Button onClick={() => setEditing("new")}>Novo eixo</Button></div>
      </div>
      {axes.data.length === 0 ? (
        <EmptyState title="Ainda não há eixos." action={<Button onClick={() => setEditing("new")}>Criar o primeiro eixo</Button>}>
          Os eixos organizam objetivos, KRs e iniciativas do plano.
        </EmptyState>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-panel border border-line bg-surface shadow-e1">
          {axes.data.map((a) => (
            <li key={a.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${a.archived_at ? "opacity-70" : ""}`}>
              <span className="size-3 shrink-0 rounded-full" style={{ background: a.color }} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{a.name} {a.archived_at && <Pill tone="gray">Arquivado</Pill>}</p>
                <p className="text-xs text-muted">
                  Grupo editor: {a.editor_group_name ?? "nenhum (somente instrutor)"} · {a.objective_count} objetivo(s)
                </p>
              </div>
              {!a.archived_at && (
                <>
                  <Button variant="secondary" onClick={() => setEditing(a)}>Editar</Button>
                  <Button variant="dangerGhost" onClick={() => setArchiving(a)}>Arquivar</Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <AxisDialog axis={editing === "new" ? null : editing} groups={(groups.data ?? []).filter((g) => !g.archived_at)} onClose={() => setEditing(null)} />
      )}
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(o) => !o && setArchiving(null)}
        title="Arquivar eixo?"
        confirmLabel="Arquivar"
        danger
        loading={busy}
        onConfirm={async () => {
          await run(() => post(`/plans/${plan.id}/axes/${archiving!.id}/archive`, { expected_version: archiving!.version }), "Eixo arquivado.");
          setArchiving(null);
        }}
      >
        <p className="text-sm">O eixo “{archiving?.name}” deixa de aparecer nas listas. Só é possível arquivar um eixo sem objetivos ativos.</p>
      </ConfirmDialog>
    </>
  );
}

function AxisDialog({ axis, groups, onClose }: { axis: Axis | null; groups: Group[]; onClose: () => void }) {
  const plan = usePlan();
  const { run } = useCommand(plan.id);
  const [blocked, setBlocked] = useState<{ affected: Affected[]; name: string } | null>(null);
  const form = useForm<z.input<typeof axisForm>>({
    resolver: zodResolver(axisForm),
    defaultValues: { name: axis?.name ?? "", color: axis?.color ?? "#1F6FB2", editor_group_id: axis?.editor_group_id ?? "" },
  });
  const submit = form.handleSubmit(async (v) => {
    const body = { name: v.name, color: v.color, editor_group_id: v.editor_group_id || null };
    const ok = await run(
      () => (axis ? patch(`/plans/${plan.id}/axes/${axis.id}`, { ...body, expected_version: axis.version }) : post(`/plans/${plan.id}/axes`, body)),
      axis ? "Eixo atualizado." : "Eixo criado.",
      (e: ApiError) => {
        if (e.code === "CONTEXT_REOPEN_REQUIRED") {
          setBlocked({ affected: (e.details?.affected as Affected[]) ?? [], name: v.name });
          return true;
        }
        applyFieldErrors(e, form.setError as never);
        return e.fieldErrors.length > 0;
      },
    );
    if (ok) onClose();
  });
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={axis ? "Editar eixo" : "Novo eixo"}
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void submit()} loading={form.formState.isSubmitting}>Salvar</Button></>}>
      <form noValidate onSubmit={submit}>
        {blocked && axis && (
          <ContextImpact planId={plan.id} isInstructor entityType="axis" entityId={axis.id} expectedVersion={axis.version}
            current={axis.name} changes={{ name: blocked.name }} affected={blocked.affected} onCancel={onClose}
            onDone={async () => { await run(async () => undefined, "Eixo renomeado. As iniciativas afetadas voltaram a rascunho."); onClose(); }} />
        )}
        <TextField label="Nome" required autoFocus error={form.formState.errors.name?.message} {...form.register("name")} />
        <TextField label="Cor" type="color" className="h-11 p-1" error={form.formState.errors.color?.message} {...form.register("color")} hint="A cor sempre acompanha o nome do eixo." />
        <SelectField label="Grupo editor" {...form.register("editor_group_id")} hint="Participantes deste grupo editam o eixo. Os demais apenas consultam.">
          <option value="">Nenhum (somente instrutor)</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </SelectField>
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
